import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { AuthContext } from '../../context/AuthContext';
import { FaMotorcycle, FaCar, FaQuestionCircle, FaToggleOn, FaToggleOff, FaChevronDown, FaChevronUp, FaUserTag, FaEdit, FaSave, FaTimes, FaPhone, FaEnvelope, FaKey } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Modal, Button as BootstrapButton } from 'react-bootstrap';
import Alert from '../common/Alert';
import FormInput from '../common/FormInput';
import Button from '../common/Button';
import IconButton from '../common/IconButton';
import CommunityIdentity from '../common/CommunityIdentity';
import useApi from '../../hooks/useApi';
import 'animate.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const UserCard = React.memo(({ user, isEditing, onStartEditingRoles, onHandleRoleChange, onHandleConfirmRoleUpdate, onCancelEditingRoles, onHandleToggleStatus, onResetPassword, allRoles, isSuperAdmin, canEditRoles, validateVehicleNo, renderVehicleIcon, tempRoles }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  console.log(`Rendering UserCard for user: ${user.id}, username: ${user.username}, community: ${user.community?.displayName || 'N/A'}, isExpanded: ${isExpanded}`);

  const toggleExpand = (e) => {
    e.stopPropagation();
    console.log(`Toggling expand for user: ${user.id}, username: ${user.username}, new state: ${!isExpanded}`);
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className="user-card animate__animated animate__fadeInUp">
      <div
        className="user-card-header"
        onClick={toggleExpand}
      >
        <div>
          <h6 className="mb-0 text-dark">
            {user.username} - {user.name}
          </h6>
          <small className="text-muted">{user.ownershipType || 'N/A'}</small>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span
            className={`badge ${
              user.status === 'ACTIVE'
                ? 'bg-success'
                : user.status === 'DISABLED'
                ? 'bg-danger'
                : user.status === 'PENDING'
                ? 'bg-warning'
                : 'bg-secondary'
            }`}
          >
            {user.status}
          </span>
          {isExpanded ? (
            <FaChevronUp size={14} className="text-muted" />
          ) : (
            <FaChevronDown size={14} className="text-muted" />
          )}
        </div>
      </div>
      {isExpanded && (
        <div className="user-card-body">
          <div className="mb-2">
            <strong className="text-dark">House No:</strong>{' '}
            <span className="text-muted">{user.houseNo || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Division:</strong>{' '}
            <span className="text-muted">{user.division || '-'}</span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Mobile No:</strong>{' '}
            <span className="contact-info">
              <a
                href={user.mobileNo ? `tel:${user.mobileNo}` : undefined}
                onClick={(e) => !user.mobileNo && e.preventDefault()}
              >
                <FaPhone
                  size={14}
                  className={`phone-icon ${!user.mobileNo ? 'disabled' : ''}`}
                />
              </a>
              <span className="text-muted">{user.mobileNo || '-'}</span>
            </span>
          </div>
          <div className="mb-2">
            <strong className="text-dark">Email:</strong>{' '}
            <span className="contact-info">
              <a
                href={user.email ? `mailto:${user.email}` : undefined}
                onClick={(e) => !user.email && e.preventDefault()}
              >
                <FaEnvelope
                  size={14}
                  className={`email-icon ${!user.email ? 'disabled' : ''}`}
                />
              </a>
              <span className="text-muted">{user.email || '-'}</span>
            </span>
          </div>
          {isSuperAdmin && (
            <div className="mb-2">
              <strong className="text-dark">Community:</strong>{' '}
              {user.community ? <CommunityIdentity community={user.community} size="xs" compact /> : 'N/A'}
            </div>
          )}
          <div className="mb-2">
            <strong className="text-dark">Vehicles:</strong>{' '}
            {user.vehicles?.length > 0 ? (
              <div className="d-flex flex-wrap gap-1">
                {user.vehicles.map((v) => {
                  validateVehicleNo(v.vehicleNo);
                  return (
                    <span key={v.id} className="badge bg-light text-dark border">
                      {v.vehicleNo} {renderVehicleIcon(v)}
                    </span>
                  );
                })}
              </div>
            ) : (
              <span className="text-muted">None</span>
            )}
          </div>
          {canEditRoles && (
            <div className="mb-2">
              <strong className="text-dark">Roles:</strong>{' '}
              {isEditing ? (
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  {allRoles.map((role) => (
                    <span
                      key={role}
                      className={`badge role-chip ${
                        tempRoles.has(role) ? 'selected bg-primary text-white' : 'bg-light text-dark border'
                      } ${role === 'SUPER_ADMIN' && !isSuperAdmin ? 'disabled' : ''}`}
                      onClick={() =>
                        role === 'SUPER_ADMIN' && !isSuperAdmin
                          ? null
                          : onHandleRoleChange(role, !tempRoles.has(role))
                      }
                      style={role === 'SUPER_ADMIN' && !isSuperAdmin ? { cursor: 'not-allowed', opacity: 0.5 } : {}}
                    >
                      {role}
                    </span>
                  ))}
                  <IconButton
                    icon={<FaSave />}
                    tooltip="Save"
                    variant="success"
                    onClick={() => onHandleConfirmRoleUpdate(user.id)}
                    size="sm"
                    className="ms-2"
                  />
                  <IconButton
                    icon={<FaTimes />}
                    tooltip="Cancel"
                    variant="secondary"
                    onClick={onCancelEditingRoles}
                    size="sm"
                    className="ms-1"
                  />
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  {user.roles && user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <span key={role} className="badge bg-primary">
                        {role}
                      </span>
                    ))
                  ) : (
                    <span className="badge bg-secondary">No Roles</span>
                  )}
                  <IconButton
                    icon={<FaEdit />}
                    tooltip="Edit Roles"
                    variant="link"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStartEditingRoles(user.id, user.roles);
                    }}
                    size="sm"
                    className="ms-1 p-0 text-muted"
                  />
                </div>
              )}
            </div>
          )}
          {(user.status === 'ACTIVE' || user.status === 'DISABLED') && (
            <div className="mt-2 d-flex gap-2">
              <IconButton
                icon={user.status === 'ACTIVE' ? <FaToggleOff /> : <FaToggleOn />}
                tooltip={user.status === 'ACTIVE' ? 'Disable' : 'Enable'}
                variant={user.status === 'ACTIVE' ? 'danger' : 'success'}
                onClick={(e) => {
                  e.stopPropagation();
                  onHandleToggleStatus(user.id, user.status);
                }}
                size="sm"
              />
              <IconButton
                icon={<FaKey />}
                tooltip="Set a temporary password"
                variant="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  onResetPassword(user);
                }}
                size="sm"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});

function ManageUsers({ isSuperAdmin, startCreate = false, onCreateClose, showAddButton = true }) {
  const { session } = useContext(AuthContext);
  const canEditRoles = false;
  const { communityId } = session || {};
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [communities, setCommunities] = useState([]);
  const [filters, setFilters] = useState({
    name: '',
    houseNo: '',
    division: '',
    status: '',
    ownershipType: '',
    communityId: '',
  });
  const [error, setError] = useState('');
  const [communityError, setCommunityError] = useState('');
  const [editingRoles, setEditingRoles] = useState(null);
  const [tempRoles, setTempRoles] = useState(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    username: '', password: '', name: '', mobileNo: '', email: '', role: 'SECURITY',
    houseNo: '', division: '', ownershipType: '', communityId: isSuperAdmin ? '' : communityId || '',
  });
  const [userIdToUpdate, setUserIdToUpdate] = useState(null);
  const { get, post, put } = useApi();

  const allRoles = ['USER', 'ADMIN', 'SECURITY', 'SUPER_ADMIN'];

  console.log('ManageUsers - isSuperAdmin:', isSuperAdmin, 'canEditRoles:', canEditRoles, 'communityId:', communityId);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchCommunities();
    }
    fetchUsers();
  }, [currentPage, filters, isSuperAdmin, communityId]);

  useEffect(() => { if (startCreate) setShowCreateModal(true); }, [startCreate]);

  const fetchCommunities = async () => {
    try {
      console.log('ManageUsers - Fetching communities from /api/communities/active');
      const response = await get('/api/communities/active');
      console.log('ManageUsers - Communities response:', response);
      if (!Array.isArray(response)) {
        console.error('Expected array for communities, got:', response);
        throw new Error('Invalid communities response format');
      }
      if (response.length === 0) {
        console.warn('No communities returned from /api/communities/active');
        setCommunityError('No communities found. You can still manage users without filtering by community.');
      } else {
        setCommunities(response);
        setCommunityError('');
      }
    } catch (err) {
      console.error('ManageUsers - Fetch communities error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      let errorMessage = 'Failed to fetch communities. You can still manage users without filtering by community.';
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please ensure you have super admin privileges.';
      } else if (err.response?.status === 404) {
        errorMessage = 'Communities endpoint not found. Please check the API configuration.';
      } else if (err.response?.status >= 500) {
        errorMessage = 'Server error while fetching communities. Please try again later.';
      }
      setCommunityError(errorMessage);
      toast.error(`Failed to fetch communities: ${err.message}`);
    }
  };

  const fetchUsers = async () => {
    try {
      const params = {
        page: currentPage,
        size: 10,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
        ...(!isSuperAdmin && communityId ? { communityId } : {}),
      };
      console.log('ManageUsers - Fetching users with params:', params);
      const response = await get('/api/admin/users/search', { params });
      console.log('ManageUsers - Users response:', response);

      if (!response.content || !Array.isArray(response.content)) {
        console.error('Expected content array, got:', response);
        throw new Error('Invalid users response format');
      }

      const uniqueUsers = [];
      const seenIds = new Set();
      response.content.forEach(user => {
        if (!seenIds.has(user.id)) {
          seenIds.add(user.id);
          uniqueUsers.push(user);
        }
      });

      setUsers(uniqueUsers);
      setTotalPages(response.totalPages || 1);
      setTotalElements(response.totalElements || 0);
      setError('');
    } catch (err) {
      console.error('ManageUsers - Fetch users error:', {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      let errorMessage = `Failed to fetch users: ${err.message}`;
      if (err.response?.status === 403) {
        errorMessage = 'Access denied. Please ensure you have admin or super admin privileges.';
      }
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      console.log('ManageUsers - Toggling status for user:', id);
      await put(`/api/admin/user/${id}/toggle-status`);
      setUsers(
        users.map((user) =>
          user.id === id
            ? {
                ...user,
                status: currentStatus === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
              }
            : user
        )
      );
      toast.success(`User status updated.`);
    } catch (err) {
      console.error('ManageUsers - Toggle status error:', err);
      setError('Failed to update user status.');
      toast.error('Failed to update user status.');
    }
  };

  const startEditingRoles = (userId, currentRoles) => {
    console.log('Starting role editing for user:', userId, 'with roles:', currentRoles);
    setEditingRoles(userId);
    setTempRoles(new Set(currentRoles || []));
  };

  const handleRoleChange = (role, checked) => {
    const updatedRoles = new Set(tempRoles);
    if (checked) {
      updatedRoles.add(role);
    } else {
      updatedRoles.delete(role);
    }
    console.log('Role change - Role:', role, 'Checked:', checked, 'Updated roles:', Array.from(updatedRoles));
    setTempRoles(updatedRoles);
  };

  const handleConfirmRoleUpdate = (userId) => {
    console.log('Attempting to confirm role update for user:', userId, 'with tempRoles:', Array.from(tempRoles));
    if (!tempRoles || tempRoles.size === 0) {
      toast.error('Please select at least one role to update.');
      return;
    }
    setUserIdToUpdate(userId);
    setShowConfirmModal(true);
  };

  const confirmRoleUpdate = async () => {
    console.log('Confirming role update for user:', userIdToUpdate, 'with roles:', Array.from(tempRoles));
    if (!userIdToUpdate || !tempRoles || tempRoles.size === 0) {
      toast.error('No roles selected or invalid user ID.');
      setShowConfirmModal(false);
      setUserIdToUpdate(null);
      return;
    }

    try {
      const response = await put(`/api/admin/user/${userIdToUpdate}/role`, Array.from(tempRoles));
      console.log('ManageUsers - Role update response:', response);
      setUsers(
        users.map((user) =>
          user.id === userIdToUpdate
            ? {
                ...user,
                roles: Array.from(tempRoles),
              }
            : user
        )
      );
      toast.success(`User roles updated to ${Array.from(tempRoles).join(', ')}.`);
    } catch (err) {
      console.error('ManageUsers - Update role error:', err);
      setError('Failed to update user roles.');
      toast.error('Failed to update user roles.');
    } finally {
      setTimeout(() => {
        setEditingRoles(null);
        setTempRoles(new Set());
        setShowConfirmModal(false);
        setUserIdToUpdate(null);
      }, 300); // Delay to ensure modal animation completes
    }
  };

  const cancelRoleUpdate = () => {
    console.log('Canceling role update');
    setShowConfirmModal(false);
    setUserIdToUpdate(null);
  };

  const cancelEditingRoles = () => {
    console.log('Canceling role editing');
    setEditingRoles(null);
    setTempRoles(new Set());
    setShowConfirmModal(false);
    setUserIdToUpdate(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    console.log('ManageUsers - Filter change:', { name, value });
    setFilters({ ...filters, [name]: value });
    setCurrentPage(0);
  };

  const clearFilters = () => {
    console.log('ManageUsers - Clearing filters');
    setFilters({
      name: '',
      houseNo: '',
      division: '',
      status: '',
      ownershipType: '',
      communityId: '',
    });
    setCurrentPage(0);
  };

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    try {
      setIsCreating(true);
      await post('/api/users', {
        ...createForm,
        roles: [createForm.role],
        communityId: isSuperAdmin ? (createForm.communityId || null) : communityId,
        ...(createForm.role !== 'USER' ? { houseNo: null, division: null, ownershipType: null } : {}),
      });
      toast.success(`${createForm.role === 'USER' ? 'Resident' : createForm.role === 'SECURITY' ? 'Security' : 'Administrator'} user created.`);
      setShowCreateModal(false);
      onCreateClose?.();
      setCreateForm({
        username: '', password: '', name: '', mobileNo: '', email: '', role: 'SECURITY',
        houseNo: '', division: '', ownershipType: '', communityId: isSuperAdmin ? '' : communityId || '',
      });
      await fetchUsers();
    } catch (err) {
      toast.error(err.message || 'Unable to create user.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleAdminPasswordReset = async (event) => {
    event.preventDefault();
    if (!resetTarget) return;
    try {
      setIsResetting(true);
      await put(`/api/users/${resetTarget.id}/reset-password`, { newPassword: resetPassword });
      toast.success(`Password reset for ${resetTarget.name || resetTarget.username}.`);
      setResetTarget(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.message || 'Unable to reset this password.');
    } finally {
      setIsResetting(false);
    }
  };

  const validateVehicleNo = (vehicleNo) => {
    const isValid = vehicleNo && vehicleNo.length === 10;
    if (!isValid) {
      console.warn('ManageUsers - Invalid vehicleNo:', vehicleNo, 'Expected 10 characters');
    }
    return isValid;
  };

  const renderVehicleIcon = (vehicle) => {
    const type = vehicle.type?.toLowerCase().replace('-', '_');
    return (
      <>
        {type === 'two_wheeler' ? (
          <FaMotorcycle size={14} color="#007bff" title="Two-Wheeler" />
        ) : type === 'four_wheeler' ? (
          <FaCar size={14} color="#28a745" title="Four-Wheeler" />
        ) : (
          <FaQuestionCircle size={14} color="#6c757d" title="Unknown" />
        )}
      </>
    );
  };

  return (
    <div className="container py-4 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div><span className="vehicle-panel-kicker">Community access</span><h2 className="mb-0 fs-4 fw-bold text-dark">Manage Users</h2></div>
        {showAddButton && <Button variant="primary" onClick={() => setShowCreateModal(true)}>Add user</Button>}
      </div>
      <div className="card shadow-sm mb-4 border-0">
        <div className="card-body">
          <h5 className="card-title fs-5 mb-3 text-dark">Filter Users</h5>
          {error && <Alert type="danger" message={error} />}
          {communityError && isSuperAdmin && <Alert type="danger" message={communityError} />}
          <form>
            <div className="row g-3">
              <div className="col-md-2">
                <FormInput
                  label="Name"
                  name="name"
                  value={filters.name}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2">
                <FormInput
                  label="House No"
                  name="houseNo"
                  value={filters.houseNo}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2">
                <FormInput
                  label="Division"
                  name="division"
                  value={filters.division}
                  onChange={handleFilterChange}
                />
              </div>
              <div className="col-md-2">
                <div className="mb-3">
                  <label htmlFor="status" className="form-label">
                    Status
                  </label>
                  <select
                    className="form-select"
                    id="status"
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                  >
                    <option value="">All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="DISABLED">Disabled</option>
                    <option value="PENDING">Pending</option>
                    <option value="REJECTED">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="col-md-2">
                <FormInput
                  label="Ownership Type"
                  name="ownershipType"
                  value={filters.ownershipType}
                  onChange={handleFilterChange}
                />
              </div>
              {isSuperAdmin && (
                <div className="col-md-2">
                  <div className="mb-3">
                    <label htmlFor="communityId" className="form-label">
                      Community
                    </label>
                    <select
                      className="form-select"
                      id="communityId"
                      name="communityId"
                      value={filters.communityId}
                      onChange={handleFilterChange}
                      disabled={communities.length === 0}
                    >
                      <option value="">All</option>
                      {communities.map((community) => (
                        <option key={community.id} value={community.id}>
                          {community.displayName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              <div className="col-md-2 d-flex align-items-end">
                <Button variant="outline-secondary" onClick={clearFilters}>
                  Clear
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      {users.length === 0 ? (
        <Alert type="info" message="No users found." />
      ) : (
        <>
          <style>
            {`
              .user-card {
                border: none;
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
                transition: transform 0.2s, box-shadow 0.2s;
                background-color: #fff;
                overflow: hidden;
              }
              .user-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
              }
              .user-card-header {
                background-color: #f8f9fa;
                padding: 12px 16px;
                border-bottom: none;
                display: flex;
                justify-content: space-between;
                align-items: center;
                cursor: pointer;
              }
              .user-card-body {
                padding: 16px;
                background-color: #fff;
              }
              .user-card-footer {
                background-color: #f8f9fa;
                padding: 8px 16px;
                border-top: none;
                text-align: center;
              }
              .user-card .badge {
                font-size: 0.75rem;
                padding: 4px 8px;
                border-radius: 12px;
                margin-right: 4px;
                margin-bottom: 4px;
              }
              .user-card .role-chip {
                cursor: pointer;
                transition: background-color 0.2s;
              }
              .user-card .role-chip:hover {
                background-color: #e9ecef !important;
              }
              .user-card .role-chip.selected {
                background-color: #007bff !important;
                color: #fff !important;
              }
              .user-card .role-chip.disabled {
                cursor: not-allowed;
                opacity: 0.5;
              }
              .user-grid {
                display: flex;
                flex-wrap: wrap;
                gap: 16px;
              }
              .user-card {
                flex: 1 1 300px;
                max-width: 300px;
              }
              @media (max-width: 576px) {
                .user-card {
                  flex: 1 1 100%;
                  max-width: 100%;
                }
              }
              .contact-info {
                display: flex;
                align-items: center;
                gap: 8px;
              }
              .contact-info .phone-icon {
                color: #28a745;
                cursor: pointer;
              }
              .contact-info .phone-icon.disabled {
                color: #6c757d;
                cursor: not-allowed;
              }
              .contact-info .email-icon {
                color: #007bff;
                cursor: pointer;
              }
              .contact-info .email-icon.disabled {
                color: #6c757d;
                cursor: not-allowed;
              }
              .modal-content {
                border-radius: 12px;
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              }
              .modal-header {
                border-bottom: none;
                padding-bottom: 0;
              }
              .modal-title {
                font-size: 1.25rem;
                font-weight: 600;
                color: #343a40;
              }
              .modal-body {
                font-size: 1rem;
                color: #6c757d;
              }
              .modal-footer {
                border-top: none;
                padding-top: 0;
              }
              .btn-confirm {
                background-color: #007bff;
                border-color: #007bff;
                transition: background-color 0.2s;
              }
              .btn-confirm:hover {
                background-color: #0056b3;
                border-color: #0056b3;
              }
              .btn-cancel {
                background-color: #6c757d;
                border-color: #6c757d;
                transition: background-color 0.2s;
              }
              .btn-cancel:hover {
                background-color: #5a6268;
                border-color: #5a6268;
              }
            `}
          </style>
          <div className="user-grid">
            {users.map((user) => {
              const isEditing = editingRoles === user.id;
              return (
                <UserCard
                  key={user.id}
                  user={user}
                  isEditing={isEditing}
                  onStartEditingRoles={startEditingRoles}
                  onHandleRoleChange={handleRoleChange}
                  onHandleConfirmRoleUpdate={handleConfirmRoleUpdate}
                  onCancelEditingRoles={cancelEditingRoles}
                  onHandleToggleStatus={handleToggleStatus}
                  onResetPassword={setResetTarget}
                  allRoles={allRoles}
                  isSuperAdmin={isSuperAdmin}
                  canEditRoles={canEditRoles}
                  validateVehicleNo={validateVehicleNo}
                  renderVehicleIcon={renderVehicleIcon}
                  tempRoles={tempRoles}
                />
              );
            })}
          </div>
          {totalPages > 1 && (
            <>
              <nav className="mt-4">
                <ul className="pagination justify-content-center flex-wrap">
                  <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                    <Button
                      variant="link"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      className="page-link"
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                  </li>
                  {[...Array(Math.min(totalPages, 5)).keys()].map((i) => {
                    const pageNum = i + Math.max(0, currentPage - 2);
                    if (pageNum >= totalPages) return null;
                    return (
                      <li
                        key={pageNum}
                        className={`page-item ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        <Button
                          variant="link"
                          onClick={() => setCurrentPage(pageNum)}
                          className="page-link"
                        >
                          {pageNum + 1}
                        </Button>
                      </li>
                    );
                  })}
                  <li
                    className={`page-item ${currentPage >= totalPages - 1 ? 'disabled' : ''}`}
                  >
                    <Button
                      variant="link"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      className="page-link"
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </li>
                </ul>
              </nav>
              <p className="text-center text-muted">
                Showing {users.length} of {totalElements} users
              </p>
            </>
          )}
        </>
      )}

      <Modal
        show={showConfirmModal}
        onHide={cancelRoleUpdate}
        centered
        className="animate__animated animate__fadeIn"
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Role Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to update this user's roles to{' '}
          <strong>{tempRoles.size > 0 ? Array.from(tempRoles).join(', ') : 'None'}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton
            variant="secondary"
            onClick={cancelRoleUpdate}
            className="btn-cancel"
          >
            Cancel
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={confirmRoleUpdate}
            className="btn-confirm"
          >
            Confirm
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(resetTarget)} onHide={() => { setResetTarget(null); setResetPassword(''); }} centered>
        <form onSubmit={handleAdminPasswordReset}>
          <Modal.Header closeButton><Modal.Title>Reset user password</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted">Set a temporary password for <strong>{resetTarget?.name || resetTarget?.username}</strong>. Share it securely and ask the user to change it after signing in.</p>
            <label className="form-label w-100">New temporary password
              <input required minLength="8" type="password" autoComplete="new-password" className="form-control mt-1" value={resetPassword} onChange={(event) => setResetPassword(event.target.value)} />
            </label>
          </Modal.Body>
          <Modal.Footer>
            <BootstrapButton variant="secondary" onClick={() => { setResetTarget(null); setResetPassword(''); }}>Cancel</BootstrapButton>
            <BootstrapButton type="submit" variant="warning" disabled={isResetting}>{isResetting ? 'Resetting…' : 'Reset password'}</BootstrapButton>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showCreateModal} onHide={() => { setShowCreateModal(false); onCreateClose?.(); }} centered size="lg">
        <form onSubmit={handleCreateUser}>
          <Modal.Header closeButton><Modal.Title>Create user account</Modal.Title></Modal.Header>
          <Modal.Body>
            <p className="text-muted">Security staff and administrators are operational users, so residence details are not required.</p>
            <div className="row g-3">
              <label className="col-md-6 form-label">Account type<select className="form-select mt-1" name="role" value={createForm.role} onChange={handleCreateChange}><option value="SECURITY">Security staff</option><option value="ADMIN">Community admin</option><option value="USER">Resident</option>{isSuperAdmin&&<option value="SUPER_ADMIN">Platform super admin</option>}</select></label>
              {isSuperAdmin && <label className="col-md-6 form-label">Community {createForm.role==='SUPER_ADMIN'&&<span className="text-muted">(optional)</span>}<select required={createForm.role!=='SUPER_ADMIN'} className="form-select mt-1" name="communityId" value={createForm.communityId} onChange={handleCreateChange}><option value="">{createForm.role==='SUPER_ADMIN'?'No community - platform only':'Select community'}</option>{communities.map(item => <option key={item.id} value={item.id}>{item.displayName}</option>)}</select></label>}
              <label className="col-md-6 form-label">Full name<input required minLength="2" maxLength="50" className="form-control mt-1" name="name" value={createForm.name} onChange={handleCreateChange}/></label>
              <label className="col-md-6 form-label">Mobile number<input required pattern="[0-9]{10}" className="form-control mt-1" name="mobileNo" value={createForm.mobileNo} onChange={handleCreateChange}/></label>
              <label className="col-md-6 form-label">Username<input required minLength="4" maxLength="50" autoComplete="off" className="form-control mt-1" name="username" value={createForm.username} onChange={handleCreateChange}/></label>
              <label className="col-md-6 form-label">Initial password<input required minLength="8" type="password" autoComplete="new-password" className="form-control mt-1" name="password" value={createForm.password} onChange={handleCreateChange}/></label>
              <label className="col-12 form-label">Email (optional)<input type="email" className="form-control mt-1" name="email" value={createForm.email} onChange={handleCreateChange}/></label>
              {createForm.role === 'USER' && <>
                <label className="col-md-4 form-label">House number<input required className="form-control mt-1" name="houseNo" value={createForm.houseNo} onChange={handleCreateChange}/></label>
                <label className="col-md-4 form-label">Division<input required className="form-control mt-1" name="division" value={createForm.division} onChange={handleCreateChange}/></label>
                <label className="col-md-4 form-label">Ownership<select required className="form-select mt-1" name="ownershipType" value={createForm.ownershipType} onChange={handleCreateChange}><option value="">Select</option><option value="OWNER">Owner</option><option value="TENANT">Tenant</option></select></label>
              </>}
            </div>
          </Modal.Body>
          <Modal.Footer><BootstrapButton variant="secondary" onClick={() => { setShowCreateModal(false); onCreateClose?.(); }}>Cancel</BootstrapButton><BootstrapButton type="submit" variant="primary" disabled={isCreating}>{isCreating ? 'Creating…' : 'Create user'}</BootstrapButton></Modal.Footer>
        </form>
      </Modal>
    </div>
  );
}

ManageUsers.propTypes = {
  isSuperAdmin: PropTypes.bool.isRequired,
  startCreate: PropTypes.bool,
  onCreateClose: PropTypes.func,
  showAddButton: PropTypes.bool,
};

export default ManageUsers;
